<script>
import { ref, toRefs, inject, onMounted, onBeforeUnmount, computed, watch } from '@vue/composition-api';
import useMapUtils from '@/compostables/useMapUtils';
import useGeneralUtils from '@/compostables/useGeneralUtils';
import { MAP_ZOOM_LEVEL_BREAKPOINT } from '@/utils/constants';
import { getSpeedMarkerColour } from '@/helpers';

export default {
    name: 'LocationHistoryMarker',
    props: {
        locationHistoryDetails: {
            type: Object,
            required: true
        },
        pinnedDriverDetails: {
            type: Object,
            default: () => {}
        },
        zoomLevel: {
            type: Number,
            default: null
        }
    },
    setup(props, { root }) {
        const { locationHistoryDetails } = props;
        const {
            coordinates: { latitude, longitude },
            speed,
            minutesFromMidnight
        } = locationHistoryDetails;
        const { setMarkers } = useMapUtils(root);
        const { speedMultiplier } = useGeneralUtils(root);
        const { getters } = inject('vuex-store');
        const google = inject('google');
        const map = inject('map');
        const bounds = inject('bounds');
        const isStreetView = inject('isStreetView');
        const marker = ref(null);
        const isLocationHistoryToggled = computed(() => getters['map/isLocationHistoryToggled']);

        const { zoomLevel } = toRefs(props);

        const timeWindow = computed(() => getters['map/timeWindow']);
        const { pinnedDriverDetails } = toRefs(props);

        onMounted(() => {
            const { speedUnits } = getters['user/user'];
            // $todo: Check this and move to parent as a prop
            const convertedSpeed = Number.isNaN(speed) ? 0 : Math.round(speed * speedMultiplier.value[speedUnits]);
            const markerToShow = getSpeedMarkerColour(convertedSpeed);

            marker.value = setMarkers(google.value, map.value, latitude, longitude, 'timeSeries', markerToShow);

            bounds.value.extend(marker.value.getPosition());
            map.value.fitBounds(bounds.value);

            marker.value.addListener('click', () => {
                const data = Object.assign({}, pinnedDriverDetails.value, locationHistoryDetails);
                root.$emit('marker-clicked', {
                    data,
                    marker: marker.value,
                    type: 'locationHistory'
                });
            });

            /**
             * Dont display marker if:
             * 1. it's not withing the timeWindow range
             * 2. if location history is not toggled.
             * 3. zoom threshhold is not reached
             */
            const [start, end] = timeWindow.value;
            if (
                (start < minutesFromMidnight && minutesFromMidnight > end) ||
                !isLocationHistoryToggled.value ||
                zoomLevel.value <= MAP_ZOOM_LEVEL_BREAKPOINT
            )
                marker.value.setMap(null);
        });

        onBeforeUnmount(() => {
            marker.value.setMap(null);
            marker.value = null;
        });

        const isPlaying = computed(() => getters['map/isPlaying']);

        watch(isPlaying, (newVal) => {
            if (newVal) {
                marker.value.setMap(null);
            } else {
                const zoomLevel = map.value.getZoom();
                if (zoomLevel <= MAP_ZOOM_LEVEL_BREAKPOINT) {
                    marker.value.setMap(null);
                } else {
                    marker.value.setMap(map.value);
                    bounds.value.extend(marker.value.getPosition());
                    map.value.fitBounds(bounds.value);
                }
            }
        });

        watch(isStreetView, (newVal) => {
            marker.value.setMap(!newVal && isLocationHistoryToggled.value ? map.value : null);
        });

        watch(isLocationHistoryToggled, (newVal) => {
            marker.value.setMap(newVal ? map.value : null);
        });

        watch(timeWindow, ([start, end]) => {
            if (minutesFromMidnight > end) 
                marker.value.setMap(null);
            else {
                const zoomLevel = map.value.getZoom();
                if (zoomLevel <= MAP_ZOOM_LEVEL_BREAKPOINT) {
                    marker.value.setMap(null);
                } else {
                    marker.value.setMap(map.value);
                    bounds.value.extend(marker.value.getPosition());
                    map.value.fitBounds(bounds.value);
                }
            }
        });

        // check if zoom level is greater than breakpoint
        watch(zoomLevel, (newVal) => {
            if (!isLocationHistoryToggled.value) {
                marker.value.setMap(null);
                return true;
            }
            // if less than breakpoint, don't show marker
            if (newVal <= MAP_ZOOM_LEVEL_BREAKPOINT) 
                marker.value.setMap(null);
            // if greater than breakpoint, show marker if it is within bounds.
            else {
                marker.value.setMap(bounds.value.contains(marker.value.getPosition()) ? map.value : null);
                // dont display marker if it's not withing the timeWindow range
                const [start, end] = timeWindow.value;
                if (start < minutesFromMidnight && minutesFromMidnight > end) 
                    marker.value.setMap(null);
            }
            return true;
        });

        return {
            marker
        };
    },
    render: () => null
};
</script>
